import React from 'react';
import { useEditMode } from '../../../shared/hooks/useEditMode';
import { useCareerListStore } from '../../../shared/store/careerStore';
import { StyledDivComponent, StyledInputComponent } from '../../../shared/dto/StyleCompRepository';
import HeadlessInput from '../../../shared/component/headless/input/HeadlessInput';
import HeadlessTextArea from '../../../shared/component/headless/textarea/HeadlessTextArea';
import { MemberInfoValueTextArea } from '../../../shared/component/headless/textarea/TextAreaBody';
import { CareerPeriodData, CareerPointData } from '../../../shared/dto/EntityRepository';
import { useValidation } from '../../../shared/hooks/useValidation';
import { TextAlignment } from '../../../shared/enum/EnumRepository';
import { CAREER_CONTENT_MAX_ROWS } from '../../../shared/constants/limits';

interface MoleculeValueProps {
  careerId: string;
  value: string | number;
  targetField: keyof CareerPointData | keyof CareerPeriodData;
  inputStyle: StyledInputComponent;
  StyledDiv: StyledDivComponent;
  $alignment?: TextAlignment;
  placeholder: string;
}

const isMultilineContentField = (
  targetField: MoleculeValueProps['targetField'],
): targetField is 'content' | 'contentEn' =>
  targetField === 'content' || targetField === 'contentEn';

const limitToMaxRows = (value: string, maxRows: number): string => {
  const lines = value.split('\n');
  if (lines.length <= maxRows) return value;
  return lines.slice(0, maxRows).join('\n');
};

const MoleculeValue: React.FC<MoleculeValueProps> = ({
  careerId,
  value,
  targetField,
  inputStyle,
  StyledDiv,
  $alignment,
  placeholder
}) => {
  const { isEditMode } = useEditMode();
  const updateCareerLocal = useCareerListStore(
    (state) => state.updateCareerLocal,
  );
  const { checkType } = useValidation();

  const handleChange = (nextValue: string) => {
    if (targetField === 'content') {
      updateCareerLocal(careerId, { content: nextValue })
    } else if (targetField === 'contentEn') {
      updateCareerLocal(careerId, { contentEn: nextValue })
    } else if (targetField === 'relatedUrl') {
      updateCareerLocal(careerId, { relatedUrl: nextValue })
    } else if (targetField === 'currentLabelOrig') {
      updateCareerLocal(careerId, { currentLabelOrig: nextValue })
    } else if (targetField === 'startYear') {
      if (checkType('startYear', nextValue))
        updateCareerLocal(careerId, { startYear: nextValue })
    } else if (targetField === 'startMonth') {
      if (checkType('startMonth', nextValue))
        updateCareerLocal(careerId, { startMonth: nextValue })
    } else if (targetField === 'startDay') {
      if (checkType('startDay', nextValue))
        updateCareerLocal(careerId, { startDay: nextValue })
    } else if (targetField === 'endYear') {
      if (checkType('endYear', nextValue))
        updateCareerLocal(careerId, { endYear: nextValue })
    } else if (targetField === 'endMonth') {
      if (checkType('endMonth', nextValue))
        updateCareerLocal(careerId, { endMonth: nextValue })
    } else if (targetField === 'endDay') {
      if (checkType('endDay', nextValue))
        updateCareerLocal(careerId, { endDay: nextValue })
    }
  };

  const handleTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    handleChange(limitToMaxRows(e.target.value, CAREER_CONTENT_MAX_ROWS));
  };

  return (
    <>
      {isEditMode ? (
        isMultilineContentField(targetField) ? (
          <HeadlessTextArea
            alignment={$alignment || TextAlignment.LEFT}
            content={String(value)}
            placeholder={placeholder}
            handleChange={handleTextAreaChange}
            StyledTextArea={MemberInfoValueTextArea}
            $minRows={1}
            $maxRows={CAREER_CONTENT_MAX_ROWS}
          />
        ) : (
          <HeadlessInput
            value={value}
            placeholder={placeholder}
            handleChange={(e) => handleChange(e.target.value)}
            StyledInput={inputStyle}
            $alignment={$alignment || TextAlignment.CENTER}
          />
        )
      ) : (
        <StyledDiv>{value}</StyledDiv>
      )}
    </>
  );
};


export default MoleculeValue;
